import { isRef, ref, Ref } from "vue";
import mitt from "mitt";

type EventType = "updated" | "added" | "removed" | "changed" | "replaced";
type EventHandler<T> = (item: T, changeType?: string) => void;

interface ListOptions<T> {
  max?: number;
  unique?: boolean;
  compare?: (a: T, b: T) => boolean;
  key?: (item: T) => any;
}

interface ChangeLog<T> {
  type: "append-all" | "add" | "update" | "remove" | "replaced";
  item?: T | T[];
  timestamp: Date;
}

export class ListError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ListError";
  }
}

export class ListManager<T> {
  private items: Ref<T[]>;
  private options: Required<ListOptions<T>>;
  private emitter = mitt<Record<EventType, T | ChangeLog<T>>>();
  private changeLog: ChangeLog<T>[] = [];

  constructor(initialItems: T[] = [], options: ListOptions<T> = {}) {
    this.items = isRef(initialItems) ? (initialItems as Ref<T[]>) : (ref(initialItems) as Ref<T[]>);
    this.options = {
      max: options.max ?? Infinity,
      unique: options.unique ?? false,
      compare: options.compare ?? this.defaultCompare,
      key: options.key ?? ((item: T) => item),
    };
  }

  private defaultCompare = (a: T, b: T): boolean => {
    const keyA = this.options.key(a);
    const keyB = this.options.key(b);

    return keyA === keyB;
  };

  private validate(item: T): void {
    if (item === null || item === undefined) {
      throw new ListError("Item cannot be null or undefined.");
    }

    if (this.isMax()) {
      throw new ListError("Cannot add more items. Maximum limit reached.");
    }
  }

  private requiredUniqueItem(item: T) {
    if (this.options.unique && this.contains(item)) {
      throw new ListError("Item already exists in the list.");
    }
  }

  isMax(): boolean {
    return this.items.value.length >= this.options.max;
  }

  contains(item: T): boolean {
    return this.items.value.some((existingItem) => this.options.compare(existingItem, item));
  }

  findIndex(item: T): number {
    return this.items.value.findIndex((existingItem) => this.options.compare(existingItem, item));
  }

  get(index: number): T | undefined {
    return this.items.value[index];
  }

  put(item: T, index?: number | undefined): void {
    this.validate(item);

    typeof index === "number" ? this.update(index, item) : this.append(item);
  }

  append(item: T): void {
    this.validate(item);
    this.requiredUniqueItem(item);
    this.items.value.push(item);
    this.emitter.emit("added", item);
    this.logChange("add", item);
  }

  appendAll(items: T[]): void {
    items.forEach((item) => this.append(item));
    this.logChange("append-all");
  }

  update(index: number, item: T): void {
    if (item === null || item === undefined) {
      throw new ListError("Item cannot be null or undefined.");
    }

    if (index >= 0 && index < this.items.value.length) {
      if (this.options.unique) {
        const existingIndex = this.items.value.findIndex((existingItem) => this.options.compare(existingItem, item));

        if (existingIndex > -1 && existingIndex !== index) {
          throw new ListError("Item already exists in the list.");
        }
      }

      this.items.value[index] = item;
      this.emitter.emit("updated", item);
      this.logChange("update", item);
    } else {
      throw new ListError("Invalid index for update operation.");
    }
  }

  remove(index: number): void {
    if (index < 0 || index >= this.items.value.length) {
      throw new ListError("Invalid index for remove operation.");
    }

    const item = this.items.value[index];

    this.items.value.splice(index, 1);
    this.emitter.emit("removed", item);
    this.logChange("remove", item);
  }

  /**
   * 두 아이템의 위치를 교환합니다.
   * @param index1 첫 번째 아이템의 인덱스
   * @param index2 두 번째 아이템의 인덱스
   * @throws {ListError} 유효하지 않은 인덱스일 경우
   */
  replace(index1: number, index2: number): void {
    if (!this.isValidIndex(index1) || !this.isValidIndex(index2)) {
      throw new ListError("Invalid index for replace operation.");
    }

    // 같은 인덱스면 무시
    if (index1 === index2) return;

    // 반응성 유지를 위해 새 배열 생성
    const newItems = [...this.items.value];

    // 두 아이템 교환
    [newItems[index1], newItems[index2]] = [newItems[index2], newItems[index1]];

    // 전체 배열을 새로 할당하여 반응성 트리거
    this.items.value = newItems;

    // 이벤트 발생
    this.logChange("replaced", [this.items.value[index1], this.items.value[index2]]);
  }

  /**
   * 주어진 인덱스가 유효한지 확인합니다.
   */
  private isValidIndex(index: number): boolean {
    return index >= 0 && index < this.items.value.length;
  }

  clear(): void {
    this.items.value.forEach((item) => this.logChange("remove", item));
    this.items.value = [];
  }

  isEmpty(): boolean {
    return this.items.value.length === 0;
  }

  size(): number {
    return this.items.value.length;
  }

  toArray(): T[] {
    return [...this.items.value];
  }

  getChangeLog(): ChangeLog<T>[] {
    return [...this.changeLog];
  }

  on(event: EventType, handler: EventHandler<T>): void {
    this.emitter.on(event, handler as any);
  }

  off(event: EventType, handler: EventHandler<T>): void {
    this.emitter.off(event, handler as any);
  }

  filter(predicate: (item: T) => boolean): T[] {
    return this.items.value.filter(predicate);
  }

  find(predicate: (item: T) => boolean): T | undefined {
    return this.items.value.find(predicate);
  }

  map<U>(callback: (item: T) => U): U[] {
    return this.items.value.map(callback);
  }

  model(): Ref<T[]> {
    return this.items;
  }

  private logChange(type: "append-all" | "add" | "update" | "remove" | "replaced", item?: T | T[]): void {
    const change: ChangeLog<T> = { type, item, timestamp: new Date() };
    this.changeLog.push(change);
    this.emitter.emit("changed", change);
  }
}

export function useList<T>(initialItems: T[] = [], options: ListOptions<T> = {}): ListManager<T> {
  return new ListManager<T>(initialItems, options);
}
